import { format } from "date-fns";
import { Trash2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import type { Draft } from "../../types/order.types";

interface DraftCardProps {
  draft: Draft;
  onUse: () => void;
  onDelete: () => void;
}

// Outbound fields come back as time-only strings (e.g. "10:04"); returnFlight
// fields come back as full ISO datetimes (e.g. "2026-08-20T08:15:00+05:00").
// Dates aren't shown here since applying a draft never pre-fills the date
// fields in the order form — only the flight times matter for the card.
function formatFlightTime(value: string): string {
  return /^\d{4}-\d{2}-\d{2}T/.test(value) ? format(new Date(value), "HH:mm") : value;
}

interface FlightLineProps {
  flightNumber: string;
  departureAt: string;
  arrivalAt: string;
}

function FlightLine({ flightNumber, departureAt, arrivalAt }: FlightLineProps) {
  return (
    <div className="text-sm text-muted-foreground">
      {flightNumber} • {formatFlightTime(departureAt)} → {formatFlightTime(arrivalAt)}
    </div>
  );
}

export function DraftCard({ draft, onUse, onDelete }: DraftCardProps) {
  return (
    <div className="border rounded-lg p-3 space-y-1 bg-white">
      <div className="font-medium text-sm">
        {draft.from.city} — {draft.to.city}
        {draft.returnFlight && ` — ${draft.returnFlight.to.city}`}
      </div>
      <FlightLine
        flightNumber={draft.flightNumber}
        departureAt={draft.departureAt}
        arrivalAt={draft.arrivalAt}
      />
      {draft.returnFlight && (
        <FlightLine
          flightNumber={draft.returnFlight.flightNumber}
          departureAt={draft.returnFlight.departureAt}
          arrivalAt={draft.returnFlight.arrivalAt}
        />
      )}
      {draft.createdAt && (
        <div className="text-xs text-muted-foreground">
          Создан: {format(new Date(draft.createdAt), "dd.MM.yyyy HH:mm")}
        </div>
      )}
      <div className="flex items-center justify-between pt-1">
        <Button variant="outline" size="sm" onClick={onUse}>
          Использовать
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-destructive hover:text-destructive">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
